/**
 * Carriages (รถม้า, see docs/CARRIAGE.md). Every town and school compound has a carriage station:
 * a station master, a coach with its horse, and a signpost. Talking to the master (or the coach)
 * opens the list of routes; a rider pays gold by the distance and is carried to any other station
 * they have already reached on foot. Reaching a zone with a station on foot unlocks it.
 *
 * The ride is World state, driven by commands and time like everything else (multiplayer-ready):
 * board → ride (skippable after a moment) → arrive, which pays the fare and sets off through the
 * same zone switch as the warp circles (`World.travel`). The destination's World reads the arrival
 * from the save and sets the rider down at its station's door (alight). While aboard the rider is
 * safe and does nothing but ride; the party rides along.
 *
 * On a region map (OPEN_WORLD.md §2.2, §2.4) the region's stops (`ROAD_STOPS`) stand on its roads
 * too, and a ride between two of them is a coach driving the road (`region/roads.ts`) at
 * `CARRIAGE.roadSpeed`, with no zone switch: board → ride (the coach on the road, skippable) →
 * arrive (paid) → alight at the destination's door. A skipped ride ends exactly as a full one.
 *
 * Tuning and stations are data/carriage.ts.
 */
import { CLOSED_NOTICE, zoneEnabled } from './data/enabledZones';
import {
  CARRIAGE,
  fareBetween,
  hasStation,
  liBetween,
  rideSeconds,
  roadFare,
  roadLi,
  roadSeconds,
  roadStopsOf,
  ROAD_STOPS,
  STATION_LIST,
  STATIONS,
  type RoadStopDef,
  type StationDef,
  type StationSpot,
} from './data/carriage';
import { REGIONS } from './region/zone';
import { regionRoads, roadGraphOf, routeTo, stationDistance, walkLength } from './region/roads';
import { FELLOWS } from './data/party';
import { combatBlockText } from './data/combat';
import { isZoneId, schoolZone, START, ZONES, type ZoneId } from './data/zones';
import { screenFacing, screenHeading } from './iso';
import { PK } from './karma';
import { isBorder, setTileAt, Tile, tileAt, type GameMap, type Point } from './map';
import { freeAt, nearestFloor } from './path';
import type { PlayerProgress } from './progress';
import type { Entity, EntityKind, PlayerCommand, WorldEvent } from './world';

// ───────────────────────── saved state ─────────────────────────

/** What carriages keep in the save. */
export interface CarriageState {
  /** Zones whose station the player has reached (only zones with a station), in discovery order. */
  stations: ZoneId[];
  /** Set while a ride's zone switch is under way: where from, and the party aboard (fellow ids). */
  arriving: { from: ZoneId; riders: string[] } | null;
  /** Rides taken, ever. */
  trips: number;
  /** Stops on a region's roads reached on foot (not the village's own, which is its zone's station). */
  stops?: string[];
}

export const newCarriage = (start: ZoneId = START): CarriageState => ({
  stations: hasStation(start) ? [start] : [],
  arriving: null,
  trips: 0,
});

/** Stations kept in data order, unknown or station-less zones dropped, each once. */
const stationsOf = (zones: readonly unknown[]): ZoneId[] =>
  STATION_LIST.map((s) => s.zone).filter((z) => zones.includes(z));

/**
 * A saved carriage state kept within its limits. Saves from before carriages unlock what the
 * character must already have reached on foot: the starting village, where they stand now, and
 * their school's compound (joined in person).
 */
export function parseCarriage(
  v: unknown,
  legacy: { location: ZoneId; school: string | null },
): CarriageState {
  if (!v || typeof v !== 'object') {
    const school = legacy.school ? schoolZone(legacy.school)?.id : undefined;
    return { stations: stationsOf([START, legacy.location, school]), arriving: null, trips: 0 };
  }
  const r = v as Record<string, unknown>;
  const a = r.arriving && typeof r.arriving === 'object' ? (r.arriving as Record<string, unknown>) : null;
  const riders = Array.isArray(a?.riders)
    ? [...new Set(a.riders.filter((f): f is string => typeof f === 'string' && !!FELLOWS[f]))]
    : [];
  const stops = stopsOf(Array.isArray(r.stops) ? r.stops : []);
  return {
    stations: stationsOf(Array.isArray(r.stations) ? r.stations : []),
    arriving: a && isZoneId(a.from) ? { from: a.from, riders } : null,
    trips: typeof r.trips === 'number' && Number.isFinite(r.trips) ? Math.max(0, Math.floor(r.trips)) : 0,
    ...(stops.length ? { stops } : {}),
  };
}

/** Road stops kept in data order, the villages' own and unknown ones dropped, each once. */
const stopsOf = (ids: readonly unknown[]): string[] =>
  ROAD_STOPS.filter((s) => !s.village && ids.includes(s.id)).map((s) => s.id);

/** Whether a region's stop has been reached: the village's by its zone's station, the others on foot. */
export const stopKnown = (pr: PlayerProgress, s: RoadStopDef): boolean =>
  s.village ? pr.carriage.stations.includes(s.id as ZoneId) : !!pr.carriage.stops?.includes(s.id);

// ───────────────────────── commands and events ─────────────────────────

export type CarriageCommand =
  /**
   * Hire the coach at this station to another station (the whole party rides along). With `stop`, to
   * that stop on this region's roads (`to` is then this zone).
   */
  | { t: 'carriageBoard'; to: ZoneId; stop?: string }
  /** Skip the rest of the ride (only once it has run `skipAfter` seconds; earlier, as soon as it has). */
  | { t: 'carriageSkip' };

export type CarriagePhase = 'board' | 'ride' | 'arrive' | 'alight';

export type CarriageEvent =
  /** The station master was spoken to: show the routes from here (`stop`: this stop on a region's roads). */
  | { type: 'carriage'; phase: 'menu'; station: ZoneId; npcId: number; stop?: string }
  /** Climbing aboard: who rides (entity ids, the player first), the fare and how long each part lasts. */
  | {
      type: 'carriage';
      phase: 'board';
      from: ZoneId;
      to: ZoneId;
      fare: number;
      riders: number[];
      board: number;
      ride: number;
      /** Bound for this stop on the region's roads (no zone switch). */
      stop?: string;
    }
  /** Rolling: the ride sequence runs for `seconds` (skippable after `skipAfter`). */
  | {
      type: 'carriage';
      phase: 'ride';
      from: ZoneId;
      to: ZoneId;
      seconds: number;
      skipAfter: number;
      stop?: string;
    }
  /**
   * Pulled in at the destination: the fare is paid and the zone switch begins (a `travel` follows);
   * with `stop`, pulled in at that stop on the region's roads (an `alight` follows, no travel).
   */
  | { type: 'carriage'; phase: 'arrive'; from: ZoneId; to: ZoneId; fare: number; stop?: string }
  /** In the destination zone: stepping down at the station door (`stop`: a stop on the region's roads). */
  | {
      type: 'carriage';
      phase: 'alight';
      from: ZoneId;
      at: ZoneId;
      riders: number[];
      seconds: number;
      stop?: string;
    }
  /** Down and free to go. */
  | { type: 'carriage'; phase: 'done' }
  /** Boarding refused, or the ride called off before it set out (nothing was paid). */
  | { type: 'carriage'; phase: 'cancel'; reason: string };

/** What a rider may still do aboard (menus and the like); everything else waits for the ride. */
const ABOARD_ALLOWED = new Set<PlayerCommand['t']>([
  'carriageSkip',
  'emoji',
  'setTitle',
  'setSkin',
  'setMount',
  'setNeigong',
  'setArt',
  'equip',
  'unequip',
  'trainSkill',
  'trainNeigong',
  'trainStop',
  'trainAuto',
  'wearUniform',
  'claimBadge',
  'topUpClaim',
  'passBuy',
  'passClaim',
  'passClaimAll',
  'mallBuy',
  'partyKick',
  'partyLeave',
  'untarget',
  'gm',
]);

// ───────────────────────── rules (pure) ─────────────────────────

/** A route on the destination list. `why`: what keeps it shut (null when it can be taken now). */
export interface CarriageRoute {
  to: ZoneId;
  /** The zone and its station. */
  zone: string;
  station: string;
  fare: number;
  seconds: number;
  li: number;
  discovered: boolean;
  why: string | null;
  /** A stop on this region's roads (then `to` is this zone and `zone` is the stop's name). */
  stop?: string;
}

/**
 * Every other station from `here`, nearest first, each with its fare and whether it is open. `at`:
 * the stop on this region's roads the menu was opened at (the `menu` event's `stop`), which adds the
 * region's other stops, by road.
 */
export function routesFrom(pr: PlayerProgress, here: ZoneId, at?: string): CarriageRoute[] {
  return [
    ...STATION_LIST.filter((s) => s.zone !== here).map((s) => {
      const to = s.zone;
      const fare = fareBetween(here, to);
      const discovered = pr.carriage.stations.includes(to);
      const why = !discovered
        ? `ยังไม่เคยไปถึง ต้องเดินทางไป${ZONES[to].name}ด้วยตัวเองก่อน`
        : pr.gold < fare
          ? `ตำลึงไม่พอ (ต้องใช้ ${fare})`
          : null;
      return {
        to,
        zone: ZONES[to].name,
        station: s.name,
        fare,
        seconds: rideSeconds(here, to),
        li: liBetween(here, to),
        discovered,
        why,
      };
    }),
    ...(at ? stopRoutes(pr, here, at) : []),
  ].sort((a, b) => a.li - b.li || a.fare - b.fare);
}

/** The road from one of a region's stops to another (metres, by the road graph), or null. */
export function stopDistance(region: string, from: string, to: string): number | null {
  const g = regionRoads(region);
  return g ? stationDistance(g, from, to) : null;
}

/** The routes from stop `at` to the region's other stops. */
function stopRoutes(pr: PlayerProgress, here: ZoneId, at: string): CarriageRoute[] {
  const region = ZONES[here].region;
  if (!region || !REGIONS[region]) return [];
  return roadStopsOf(region).flatMap((s) => {
    const metres = s.id === at ? null : stopDistance(region, at, s.id);
    if (metres === null) return [];
    const fare = roadFare(metres);
    const discovered = stopKnown(pr, s);
    const why = !discovered
      ? `ยังไม่เคยไปถึง ต้องเดินทางไป${s.name}ด้วยตัวเองก่อน`
      : pr.gold < fare
        ? `ตำลึงไม่พอ (ต้องใช้ ${fare})`
        : null;
    return [
      {
        to: here,
        stop: s.id,
        zone: s.name,
        station: s.name,
        fare,
        seconds: roadSeconds(metres),
        li: roadLi(metres),
        discovered,
        why,
      },
    ];
  });
}

/** Heavy goods carried (logs and ore), counted against the coach's limit. */
export const heavyLoad = (pr: PlayerProgress): number =>
  CARRIAGE.heavyGoods.reduce((n, id) => n + (pr.trades.goods[id] ?? 0), 0);

/** What the rider is in the middle of that keeps them off a coach (from the World). */
export interface BoardContext {
  here: ZoneId;
  to: ZoneId;
  player: Entity;
  /** Standing at this zone's station (near the coach or its master). */
  near: boolean;
  /** An arena bout, a ladder duel, a dance ring under way, or inside a building. */
  busy: boolean;
  /** Already aboard (or stepping down). */
  riding: boolean;
  /** How many would ride: the hirer and the party with them (1 when left out). */
  riders?: number;
}

/** Why a rider may not board for `to` now (Thai, for the player), or null when they may. */
export function boardBlock(pr: PlayerProgress, c: BoardContext): string | null {
  if (c.riding) return 'กำลังเดินทางอยู่แล้ว';
  if (!hasStation(c.here) || !c.near) return 'ต้องอยู่ที่ท่ารถม้าจึงจะว่าจ้างรถได้';
  if (!hasStation(c.to) || c.to === c.here) return 'ไม่มีรถม้าไปทางนั้น';
  if (!pr.carriage.stations.includes(c.to))
    return `ยังไม่เคยไปถึง${ZONES[c.to].name} ต้องเดินทางไปเองก่อนครั้งแรก`;
  return riderBlock(pr, c) ?? fareBlock(pr, fareBetween(c.here, c.to));
}

const fareBlock = (pr: PlayerProgress, fare: number): string | null =>
  pr.gold < fare ? `ตำลึงไม่พอ ค่าโดยสาร ${fare} ตำลึง` : null;

/** What keeps the rider (and party) themselves off any coach, wherever bound (null when nothing). */
function riderBlock(pr: PlayerProgress, c: Pick<BoardContext, 'player' | 'busy' | 'riders'>): string | null {
  const p = c.player;
  if (p.dead) return 'สิ้นใจอยู่ ขึ้นรถไม่ได้';
  if (p.jailT > 0) return 'ติดคุกอยู่ ไปไหนไม่ได้';
  if (p.combatT > 0) return combatBlockText(p, 'ขึ้นรถม้าไม่ได้');
  if (p.pkPoints > PK.red) return 'นายท่ารถม้าไม่รับคนหัวแดง ไปลบล้างความผิดเสียก่อน';
  if (c.busy) return 'ยังติดธุระอยู่ (ประลอง ร่ายรำ หรืออยู่ในอาคาร)';
  const heavy = heavyLoad(pr);
  if (heavy > CARRIAGE.heavyLimit)
    return `สัมภาระหนักเกินไป (ไม้และแร่ ${heavy} ชิ้น รถรับได้ไม่เกิน ${CARRIAGE.heavyLimit})`;
  if (CARRIAGE.forbiddenItems.some((id) => pr.inventory.includes(id))) return 'มีของต้องห้าม รถม้าไม่รับ';
  if ((c.riders ?? 1) > CARRIAGE.seats) return `รถม้าคันหนึ่งนั่งได้ไม่เกิน ${CARRIAGE.seats} คน`;
  return null;
}

/** What a rider at stop `at` needs to hire the coach to stop `to` on the region's roads. */
export interface StopContext extends Pick<BoardContext, 'player' | 'busy' | 'riding' | 'riders'> {
  region: string;
  /** The stop the rider stands at (null: at none, or not near enough). */
  at: string | null;
  to: string;
}

/** Why a rider may not ride from stop `at` to stop `to` now (Thai), or null when they may. */
export function stopBlock(pr: PlayerProgress, c: StopContext): string | null {
  if (c.riding) return 'กำลังเดินทางอยู่แล้ว';
  if (!c.at) return 'ต้องอยู่ที่ท่ารถม้าจึงจะว่าจ้างรถได้';
  const dest = roadStopsOf(c.region).find((s) => s.id === c.to);
  const metres = dest && dest.id !== c.at ? stopDistance(c.region, c.at, dest.id) : null;
  if (!dest || metres === null) return 'ไม่มีรถม้าไปทางนั้น';
  if (!stopKnown(pr, dest)) return `ยังไม่เคยไปถึง${dest.name} ต้องเดินทางไปเองก่อนครั้งแรก`;
  return riderBlock(pr, c) ?? fareBlock(pr, roadFare(metres));
}

/** A station spot on this map: from the plaza, on the nearest open ground. */
export function stationPoint(m: GameMap, s: StationSpot): Point {
  return floorPoint(m, m.plaza.x + s.dx, m.plaza.y + s.dy);
}

/** (x, y), or the nearest open ground when it is more than a little off. */
export function floorPoint(m: GameMap, x: number, y: number): Point {
  const f = nearestFloor(m, x, y, 3);
  return f && Math.hypot(f[0] - x, f[1] - y) > 0.75 ? { x: f[0], y: f[1] } : { x, y };
}

/** Clears trees and boulders off a station's ground (never buildings, water, fields or the border). */
export function layStation(m: GameMap, st: StationDef): void {
  const [dx0, dx1, dy0, dy1] = st.clear;
  const ci = Math.floor(m.plaza.x);
  const cj = Math.floor(m.plaza.y);
  for (let j = cj + dy0; j < cj + dy1; j++)
    for (let i = ci + dx0; i < ci + dx1; i++) {
      if (isBorder(m, i, j)) continue;
      const t = tileAt(m, i, j);
      if (t !== Tile.Tree && t !== Tile.Rock) continue;
      setTileAt(m, i, j, Tile.Floor, 0);
    }
}

/** The way a station's coach stands and sets off (a unit direction on the map): along the road. */
export const roadDir = (st: StationDef): Point => ({ x: st.facing, y: 0 });

/**
 * Seat `i` (0 = the hirer's) of a coach whose middle is at `at`, facing `dir` (a unit direction on
 * the map): forward along `dir`, and to the coach's right, which is `dir` turned a quarter clockwise.
 */
export function seatPoint(at: Point, dir: Point, i: number): Point {
  const spots = CARRIAGE.seatSpots;
  const s = spots[((i % spots.length) + spots.length) % spots.length]!;
  return { x: at.x + dir.x * s.f - dir.y * s.l, y: at.y + dir.y * s.f + dir.x * s.l };
}

/**
 * How far a coach at `at` may roll along `dir` before the horse's nose or the coach meets
 * something in the way (a building, water, a tree), up to `CARRIAGE.pullOut` tiles.
 */
export function roadRoom(m: GameMap, at: Point, dir: Point): number {
  const stepLen = 0.25;
  let room = 0;
  for (let s = stepLen; s <= CARRIAGE.pullOut + 1e-9; s += stepLen) {
    const nose = CARRIAGE.noseAhead + s;
    const ok =
      freeAt(m, at.x + dir.x * nose, at.y + dir.y * nose, 0.3) &&
      freeAt(m, at.x + dir.x * (CARRIAGE.horseAhead + s), at.y + dir.y * (CARRIAGE.horseAhead + s), 0.3) &&
      freeAt(m, at.x + dir.x * s, at.y + dir.y * s, 0.3);
    if (!ok) break;
    room = s;
  }
  return room;
}

/**
 * The coach's speed after `dt` more seconds of setting off: picking up by `CARRIAGE.accel` to
 * `CARRIAGE.speed`, and no faster than it can still draw up within the `room` left ahead.
 */
export function rollSpeed(v: number, room: number, dt: number): number {
  if (room <= 0) return 0;
  return Math.max(0, Math.min(CARRIAGE.speed, v + CARRIAGE.accel * dt, Math.sqrt(2 * CARRIAGE.accel * room)));
}

// ───────────────────────── the station and the ride (World state) ─────────────────────────

/** What a carriage needs of the World. */
export interface CarriageWorld {
  readonly map: GameMap;
  readonly progress: PlayerProgress;
  readonly player: Entity;
}

export interface CarriageHooks {
  add(o: Partial<Entity> & { kind: EntityKind; x: number; y: number }): Entity;
  emit(e: WorldEvent): void;
  notice(text: string): void;
  /** The zone switch the warp circles use (save, then load the other zone). */
  travel(to: ZoneId): void;
  /** An arena bout, ladder duel or dance ring under way. */
  busy(): boolean;
  /** Party members here (entities). */
  party(): Entity[];
  /** Off the mount, out of meditation, emote and work, weapon away: ready to climb in. */
  settle(): void;
  face(e: Entity, at: Point): void;
}

/** A ride under way. */
export interface Ride {
  phase: CarriagePhase;
  from: ZoneId;
  to: ZoneId;
  fare: number;
  /** Seconds into the phase, and how long the ride itself lasts. */
  t: number;
  seconds: number;
  /** Asked to skip before it could be: arrive as soon as it may. */
  skip: boolean;
  /** Party members aboard (fellow ids). */
  riders: string[];
  /** Alighting: announced to the client yet. */
  told: boolean;
  /** Entity ids seated in the coach once it sets off (the hirer first), by seat. */
  seated: number[];
  /** On a region's roads: the stop bound for, and the coach's way there (from its stand). */
  stop?: string;
  road?: RoadDrive;
}

/** The coach's way along a region's roads: points from its stand to the stop, and the length before each. */
export interface RoadDrive {
  readonly pts: readonly Point[];
  readonly acc: readonly number[];
  readonly total: number;
}

/** A drive along `pts` (the first is the coach's stand). */
export function roadDrive(pts: readonly Point[]): RoadDrive {
  const acc = [0];
  for (let k = 1; k < pts.length; k++)
    acc.push(acc[k - 1]! + Math.hypot(pts[k]!.x - pts[k - 1]!.x, pts[k]!.y - pts[k - 1]!.y));
  return { pts, acc, total: acc[acc.length - 1]! };
}

/** Where a drive is `s` metres along, and which way it heads there (unit). */
export function driveAt(d: RoadDrive, s: number): { at: Point; dir: Point } {
  const { pts, acc } = d;
  // The segment holding s (binary search: a drive has hundreds of points).
  let lo = 1;
  let hi = pts.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (acc[mid]! < s) lo = mid + 1;
    else hi = mid;
  }
  const k = Math.max(1, lo);
  // Skip zero-length segments for the heading.
  let j = k;
  while (j < pts.length - 1 && acc[j]! - acc[j - 1]! < 1e-6) j++;
  const a = pts[j - 1]!;
  const b = pts[j]!;
  const l = Math.hypot(b.x - a.x, b.y - a.y);
  const dir = l > 1e-6 ? { x: (b.x - a.x) / l, y: (b.y - a.y) / l } : { x: 1, y: 0 };
  if (s >= d.total) return { at: { ...pts[pts.length - 1]! }, dir };
  const a0 = pts[k - 1]!;
  const b0 = pts[k]!;
  const seg = acc[k]! - acc[k - 1]!;
  const t = seg > 0 ? (s - acc[k - 1]!) / seg : 1;
  return { at: { x: a0.x + (b0.x - a0.x) * t, y: a0.y + (b0.y - a0.y) * t }, dir };
}

/** Which way (1 right, −1 left) a figure heading along `dir` faces on screen at the default camera. */
const screenFacingOf = (dir: Point): 1 | -1 => (screenFacing(dir.x, dir.y) >= 0 ? 1 : -1);

/** A station built on this map: the zone's own, or a stop on the region's roads. */
export interface CarriageSite {
  /** Its stop on the region's roads (region maps), or null for a zone's station off them. */
  readonly stop: string | null;
  readonly station: StationDef;
  readonly master: Entity | null;
  readonly coach: Entity | null;
  readonly sign: Entity | null;
  readonly door: Point;
  readonly home: Point;
  readonly dir: Point;
}

export class Carriage {
  ride: Ride | null = null;
  /** This zone's station, its master, coach and signpost (null where there is no station). */
  station: StationDef | null = null;
  master: Entity | null = null;
  coach: Entity | null = null;
  sign: Entity | null = null;
  /** Where riders board and step down. */
  door: Point | null = null;
  /** Where the coach stands at its station, and which way it faces and sets off (unit, map). */
  home: Point | null = null;
  dir: Point = { x: 1, y: 0 };
  /**
   * The coach on the road: its speed now (tiles/s), how far it has rolled from its stand (the
   * renderer turns its wheels and paces the horse by this), and the room it had to roll out in.
   */
  speed = 0;
  rolled = 0;
  room = 0;
  /** Every station on this map (the zone's, and on a region map its stops on the roads); `site` is the one in use. */
  sites: CarriageSite[] = [];
  site: CarriageSite | null = null;

  constructor(
    private readonly w: CarriageWorld,
    private readonly hooks: CarriageHooks,
  ) {}

  private get pr(): PlayerProgress {
    return this.w.progress;
  }

  /**
   * Builds this zone's station (when it has one), unlocks it, and sets a rider arriving by coach
   * down at its door. Called once as the World is made, after its people and before the party.
   */
  setup(): void {
    const m = this.w.map;
    const st = STATIONS[m.zone] ?? null;
    this.station = st;
    const arriving = this.pr.carriage.arriving;
    this.pr.carriage.arriving = null;
    if (!st) return this.layStops(null);
    layStation(m, st);
    const coach = stationPoint(m, st.coach);
    const door = stationPoint(m, st.door);
    const sign = stationPoint(m, st.sign);
    const mp = stationPoint(m, st.master);
    this.door = door;
    this.home = coach;
    this.dir = roadDir(st);
    this.coach = this.hooks.add({
      kind: 'npc',
      x: coach.x,
      y: coach.y,
      sprite: 'carriage',
      name: 'รถม้าโดยสาร',
      title: st.name,
      facing: st.facing,
      heading: screenHeading(this.dir.x, this.dir.y),
      dirX: this.dir.x,
      dirY: this.dir.y,
    });
    this.sign = this.hooks.add({
      kind: 'ambient',
      x: sign.x,
      y: sign.y,
      sprite: 'carriageSign',
      facing: st.facing,
    });
    this.master = this.hooks.add({
      kind: 'npc',
      x: mp.x,
      y: mp.y,
      sprite: st.master.sprite,
      name: st.master.name,
      title: st.master.title,
      facing: st.facing === 1 ? -1 : 1,
    });
    const site: CarriageSite = {
      stop: null,
      station: st,
      master: this.master,
      coach: this.coach,
      sign: this.sign,
      door,
      home: coach,
      dir: this.dir,
    };
    this.layStops(site);
    // Reaching a station on foot (or by coach) unlocks it.
    if (!this.pr.carriage.stations.includes(m.zone)) {
      this.pr.carriage.stations = stationsOf([...this.pr.carriage.stations, m.zone]);
      this.hooks.notice(`พบ${st.name} · เรียกรถม้ามาที่นี่ได้จากทุกท่ารถ`);
    }
    if (arriving) {
      const p = this.w.player;
      Object.assign(p, { x: door.x, y: door.y, path: null });
      this.hooks.face(p, { x: door.x - (coach.x - door.x), y: door.y - (coach.y - door.y) });
      this.ride = {
        phase: 'alight',
        from: arriving.from,
        to: m.zone,
        fare: 0,
        t: 0,
        seconds: CARRIAGE.alightSeconds,
        skip: false,
        riders: arriving.riders,
        told: false,
        seated: [],
      };
    }
  }

  /** The region whose stops stand on this map (a region map with a road graph), or null. */
  private regionId(): string | null {
    const m = this.w.map;
    const id = ZONES[m.zone]?.region;
    return id && roadGraphOf(m) ? id : null;
  }

  /**
   * The sites: the zone's own station (`zoneSite`, when it has one) and, on a region map, the stops
   * on its roads (the village's stop is the zone's station). The zone's station is in use.
   */
  private layStops(zoneSite: CarriageSite | null): void {
    const region = this.regionId();
    const stops = region ? roadStopsOf(region) : [];
    const village = stops.find((s) => s.village && s.id === this.w.map.zone);
    if (zoneSite) this.sites.push(village ? { ...zoneSite, stop: village.id } : zoneSite);
    const sketch = region ? REGIONS[region]!.sketch : null;
    for (const def of stops) {
      if (def.village) continue;
      const spot = def.at ?? sketch!.stations.find((s) => s.id === def.id);
      if (!spot) continue;
      this.sites.push(this.layStop(def, spot));
    }
    this.use(this.sites[0] ?? null);
  }

  /** A stop on the region's roads: its coach, signpost and master, at its spot. */
  private layStop(def: Exclude<RoadStopDef, { village: true }>, spot: Point): CarriageSite {
    const m = this.w.map;
    const home = floorPoint(m, spot.x, spot.y);
    const door = floorPoint(m, home.x + def.door.dx, home.y + def.door.dy);
    const sign = floorPoint(m, home.x + def.sign.dx, home.y + def.sign.dy);
    const mp = floorPoint(m, home.x + def.master.dx, home.y + def.master.dy);
    const dir = { x: def.facing, y: 0 };
    const station: StationDef = {
      zone: m.zone,
      name: def.name,
      coach: { dx: 0, dy: 0 },
      facing: def.facing,
      door: def.door,
      sign: def.sign,
      master: def.master,
      clear: [0, 0, 0, 0],
    };
    const coach = this.hooks.add({
      kind: 'npc',
      x: home.x,
      y: home.y,
      sprite: 'carriage',
      name: 'รถม้าโดยสาร',
      title: def.name,
      facing: def.facing,
      heading: screenHeading(dir.x, dir.y),
      dirX: dir.x,
      dirY: dir.y,
    });
    const signE = this.hooks.add({
      kind: 'ambient',
      x: sign.x,
      y: sign.y,
      sprite: 'carriageSign',
      facing: def.facing,
    });
    const master = this.hooks.add({
      kind: 'npc',
      x: mp.x,
      y: mp.y,
      sprite: def.master.sprite,
      name: def.master.name,
      title: def.master.title,
      facing: def.facing === 1 ? -1 : 1,
    });
    return { stop: def.id, station, master, coach, sign: signE, door, home, dir };
  }

  /** Puts a site in use: its station, people, door, stand and heading become this carriage's. */
  private use(site: CarriageSite | null): void {
    this.site = site;
    if (!site) return;
    this.station = site.station;
    this.master = site.master;
    this.coach = site.coach;
    this.sign = site.sign;
    this.door = site.door;
    this.home = site.home;
    this.dir = site.dir;
  }

  /** The site whose coach or master the player stands nearest, within reach (null: none). */
  private siteNear(reach: number = CARRIAGE.reach): CarriageSite | null {
    const p = this.w.player;
    let best: CarriageSite | null = null;
    let bd = Infinity;
    for (const s of this.sites)
      for (const e of [s.coach, s.master]) {
        const d = e ? Math.hypot(e.x - p.x, e.y - p.y) : Infinity;
        if (d <= reach && d < bd) {
          bd = d;
          best = s;
        }
      }
    return best;
  }

  /** Coming near a stop on the region's roads on foot unlocks it. */
  private discover(): void {
    if (this.sites.length < 2 || this.ride) return;
    const s = this.siteNear(CARRIAGE.discoverReach);
    const def = s?.stop ? ROAD_STOPS.find((d) => d.id === s.stop) : undefined;
    if (!def || def.village || stopKnown(this.pr, def)) return;
    this.pr.carriage.stops = stopsOf([...(this.pr.carriage.stops ?? []), def.id]);
    this.hooks.notice(`พบ${def.name} · เรียกรถม้ามาที่นี่ได้จากท่ารถบนเส้นทางเดียวกัน`);
    this.hooks.emit({ type: 'progressChanged' });
  }

  /** The station master or the coach: speaking to either opens the routes. */
  owns(e: Entity): boolean {
    return this.sites.some((s) => e === s.master || e === s.coach);
  }

  talk(npc: Entity): void {
    const site = this.sites.find((s) => npc === s.master || npc === s.coach);
    if (!site || this.ride) return;
    this.use(site);
    // The coach does not turn round to look at whoever speaks to it.
    if (site.coach) site.coach.facing = site.station.facing;
    this.hooks.emit({
      type: 'carriage',
      phase: 'menu',
      station: site.station.zone,
      npcId: npc.id,
      ...(site.stop ? { stop: site.stop } : {}),
    });
  }

  /** Standing at the station in use: near the coach or its master. */
  near(): boolean {
    const p = this.w.player;
    return [this.coach, this.master].some((e) => !!e && Math.hypot(e.x - p.x, e.y - p.y) <= CARRIAGE.reach);
  }

  /** Aboard or stepping down: the rider only rides. */
  locks(t: PlayerCommand['t']): boolean {
    return !!this.ride && !ABOARD_ALLOWED.has(t);
  }

  /** Why boarding for `to` is refused now, or null. */
  blockFor(to: ZoneId): string | null {
    if (!zoneEnabled(to)) return CLOSED_NOTICE;
    return boardBlock(this.pr, {
      here: this.w.map.zone,
      to,
      player: this.w.player,
      near: this.near(),
      busy: this.hooks.busy(),
      riding: !!this.ride,
      riders: 1 + this.hooks.party().length,
    });
  }

  /** Why boarding for stop `to` on the region's roads is refused now, or null. */
  stopBlockFor(to: string): string | null {
    const region = this.regionId();
    if (!region) return 'ไม่มีรถม้าไปทางนั้น';
    return stopBlock(this.pr, {
      region,
      at: this.near() ? (this.site?.stop ?? null) : null,
      to,
      player: this.w.player,
      busy: this.hooks.busy(),
      riding: !!this.ride,
      riders: 1 + this.hooks.party().length,
    });
  }

  command(c: CarriageCommand): void {
    if (c.t === 'carriageBoard') {
      // The station the hirer stands at is the one hired from.
      if (!this.ride) this.use(this.siteNear() ?? this.site);
      return typeof c.stop === 'string' ? this.boardStop(c.stop) : this.board(c.to);
    }
    if (c.t === 'carriageSkip') return this.skip();
  }

  private refuse(why: string): void {
    this.hooks.notice(why);
    this.hooks.emit({ type: 'carriage', phase: 'cancel', reason: why });
  }

  /** Hires the coach along the region's roads to stop `to`. */
  private boardStop(to: string): void {
    const why = this.stopBlockFor(to);
    if (why) return this.refuse(why);
    const region = this.regionId()!;
    const from = this.site!.stop!;
    const dest = this.sites.find((s) => s.stop === to);
    const home = this.home!;
    const path = dest ? routeTo(this.w.map, home.x, home.y, dest.home.x, dest.home.y) : null;
    if (!dest || !path) return this.refuse('ทางรถไปไม่ถึงที่นั่น');
    const fare = roadFare(stopDistance(region, from, to)!);
    const road = roadDrive([home, ...path.map(([x, y]) => ({ x, y }))]);
    this.start(this.w.map.zone, fare, roadSeconds(walkLength(home.x, home.y, path)), { stop: to, road });
  }

  private board(to: ZoneId): void {
    const why = isZoneId(to) ? this.blockFor(to) : 'ไม่มีรถม้าไปทางนั้น';
    if (why) return this.refuse(why);
    const from = this.w.map.zone;
    this.start(to, fareBetween(from, to), rideSeconds(from, to));
  }

  /** Climbing in: the ride begins (bound for zone `to`, or with `road` for a stop on the region's roads). */
  private start(to: ZoneId, fare: number, seconds: number, road?: { stop: string; road: RoadDrive }): void {
    const from = this.w.map.zone;
    const p = this.w.player;
    this.hooks.settle();
    const door = this.door!;
    Object.assign(p, { x: door.x, y: door.y, path: null, moving: false, targetId: null });
    if (this.coach) this.hooks.face(p, this.coach);
    const party = this.hooks.party();
    this.ride = {
      phase: 'board',
      from,
      to,
      fare,
      t: 0,
      seconds,
      skip: false,
      riders: party.flatMap((e) => (e.rival?.fellow ? [e.rival.fellow] : [])),
      told: false,
      seated: [],
      ...road,
    };
    this.hooks.emit({
      type: 'carriage',
      phase: 'board',
      from,
      to,
      fare: this.ride.fare,
      riders: [p.id, ...party.map((e) => e.id)],
      board: CARRIAGE.boardSeconds,
      ride: this.ride.seconds,
      ...(road ? { stop: road.stop } : {}),
    });
  }

  private skip(): void {
    const r = this.ride;
    if (!r || (r.phase !== 'board' && r.phase !== 'ride')) return;
    r.skip = true;
    if (r.phase === 'ride' && r.t >= CARRIAGE.skipAfter) this.arrive(r);
  }

  step(dt: number): void {
    this.discover();
    const r = this.ride;
    if (!r) return;
    r.t += dt;
    const p = this.w.player;
    if (r.phase === 'board' && this.door) {
      // Climbing in: held at the door, and safe there.
      Object.assign(p, { x: this.door.x, y: this.door.y, path: null, moving: false });
      p.invulnT = Math.max(p.invulnT, 0.25);
    }
    if (r.phase === 'ride' || r.phase === 'arrive') {
      if (r.road) this.drive(r.road, r.t);
      else this.roll(dt);
      this.seat(r);
    }
    if (r.phase === 'board' && r.t >= CARRIAGE.boardSeconds) {
      r.phase = 'ride';
      r.t = 0;
      // Everyone aboard takes a seat, and the coach sets off along the road.
      r.seated = [p.id, ...this.hooks.party().map((e) => e.id)].slice(0, CARRIAGE.seats);
      this.room = this.home && this.coach && !r.road ? roadRoom(this.w.map, this.home, this.dir) : 0;
      this.seat(r);
      this.hooks.emit({
        type: 'carriage',
        phase: 'ride',
        from: r.from,
        to: r.to,
        seconds: r.seconds,
        skipAfter: CARRIAGE.skipAfter,
        ...(r.stop ? { stop: r.stop } : {}),
      });
    } else if (r.phase === 'ride' && (r.t >= r.seconds || (r.skip && r.t >= CARRIAGE.skipAfter))) {
      this.arrive(r);
    } else if (r.phase === 'alight') {
      if (!r.told) {
        r.told = true;
        const riders = [p.id, ...this.hooks.party().map((e) => e.id)];
        this.hooks.emit({
          type: 'carriage',
          phase: 'alight',
          from: r.from,
          at: r.to,
          riders,
          seconds: CARRIAGE.alightSeconds,
          ...(r.stop ? { stop: r.stop } : {}),
        });
      }
      if (r.t >= CARRIAGE.alightSeconds) {
        this.ride = null;
        this.park();
        this.hooks.emit({ type: 'carriage', phase: 'done' });
      }
    }
  }

  /** Who sits in the coach now (entity ids, the hirer first; empty unless it is under way). */
  aboard(): readonly number[] {
    const r = this.ride;
    return r && (r.phase === 'ride' || r.phase === 'arrive') ? r.seated : [];
  }

  /** The coach rolling out of the station: speeding up, then drawing up within its room. */
  private roll(dt: number): void {
    const c = this.coach;
    const home = this.home;
    if (!c || !home) return;
    const left = this.room - this.rolled;
    this.speed = rollSpeed(this.speed, left, dt);
    this.rolled = Math.min(this.room, this.rolled + this.speed * dt);
    if (this.rolled >= this.room - 1e-6) this.speed = 0;
    Object.assign(c, {
      x: home.x + this.dir.x * this.rolled,
      y: home.y + this.dir.y * this.rolled,
      moving: this.speed > 0.01,
      heading: screenHeading(this.dir.x, this.dir.y),
      dirX: this.dir.x,
      dirY: this.dir.y,
    });
    c.facing = this.station?.facing ?? c.facing;
  }

  /** The coach on the region's road, `t` seconds out at `CARRIAGE.roadSpeed` (its wheels turn by `rolled`). */
  private drive(d: RoadDrive, t: number): void {
    const c = this.coach;
    if (!c) return;
    const s = Math.min(d.total, CARRIAGE.roadSpeed * t);
    const { at, dir } = driveAt(d, s);
    this.speed = s < d.total ? CARRIAGE.roadSpeed : 0;
    this.rolled = s;
    this.dir = dir;
    Object.assign(c, {
      x: at.x,
      y: at.y,
      moving: this.speed > 0,
      heading: screenHeading(dir.x, dir.y),
      dirX: dir.x,
      dirY: dir.y,
      facing: screenFacingOf(dir),
    });
  }

  /** Riders kept in their seats as the coach goes, safe, facing the way it does. */
  private seat(r: Ride): void {
    const c = this.coach;
    if (!c) return;
    const party = r.seated.length > 1 ? this.hooks.party() : [];
    r.seated.forEach((id, i) => {
      const e = id === this.w.player.id ? this.w.player : party.find((f) => f.id === id);
      if (!e) return;
      const s = seatPoint(c, this.dir, i);
      Object.assign(e, { x: s.x, y: s.y, path: null, moving: false, heading: c.heading, facing: c.facing });
      e.invulnT = Math.max(e.invulnT, 0.25);
    });
  }

  /** The coach back on its stand; with `riders`, those who were aboard set down at the door. */
  private park(riders: readonly number[] = []): void {
    const c = this.coach;
    const home = this.home;
    this.speed = 0;
    this.rolled = 0;
    this.room = 0;
    if (this.site) this.dir = this.site.dir;
    if (c && home)
      Object.assign(c, {
        x: home.x,
        y: home.y,
        moving: false,
        heading: screenHeading(this.dir.x, this.dir.y),
        dirX: this.dir.x,
        dirY: this.dir.y,
        facing: this.station?.facing ?? c.facing,
      });
    const door = this.door;
    if (!door) return;
    const party = this.hooks.party();
    riders.forEach((id, i) => {
      const e = id === this.w.player.id ? this.w.player : party.find((f) => f.id === id);
      if (e)
        Object.assign(e, { x: door.x - this.dir.y * i * 0.6, y: door.y + this.dir.x * i * 0.6, path: null });
    });
  }

  /** Pulled in: pay, and set off through the zone switch (the next zone's World sets them down). */
  private arrive(r: Ride): void {
    const pr = this.pr;
    if (pr.gold < r.fare) {
      this.ride = null;
      this.park(r.seated);
      const reason = `ตำลึงไม่พอ ค่าโดยสาร ${r.fare} ตำลึง`;
      this.hooks.notice(reason);
      return this.hooks.emit({ type: 'carriage', phase: 'cancel', reason });
    }
    pr.gold -= r.fare;
    pr.carriage.trips++;
    if (r.stop) return this.pullIn(r);
    pr.carriage.arriving = { from: r.from, riders: r.riders };
    r.phase = 'arrive';
    r.t = 0;
    this.hooks.emit({ type: 'carriage', phase: 'arrive', from: r.from, to: r.to, fare: r.fare });
    this.hooks.emit({ type: 'progressChanged' });
    this.hooks.travel(r.to);
  }

  /**
   * Pulled in at a stop on the region's roads (paid already): the coach goes back to its stand, the
   * riders step down at the stop's door, and that stop is the one in use. A ride skipped and a ride
   * driven to its end arrive here alike.
   */
  private pullIn(r: Ride): void {
    const dest = this.sites.find((s) => s.stop === r.stop) ?? null;
    this.hooks.emit({
      type: 'carriage',
      phase: 'arrive',
      from: r.from,
      to: r.to,
      fare: r.fare,
      stop: r.stop!,
    });
    this.hooks.emit({ type: 'progressChanged' });
    const seated = r.seated;
    this.park();
    this.use(dest);
    this.park(seated);
    const p = this.w.player;
    const door = this.door;
    if (door && this.home)
      this.hooks.face(p, { x: door.x - (this.home.x - door.x), y: door.y - (this.home.y - door.y) });
    Object.assign(r, { phase: 'alight', t: 0, told: false, seconds: CARRIAGE.alightSeconds, seated: [] });
    delete r.road;
  }
}
